import React, { useState, useEffect, useCallback } from 'react';
import { getSupabase } from './lib/supabase.js';
import Home from './screens/Home.jsx';
import StepDetail from './screens/StepDetail.jsx';
import Settlements from './screens/Settlements.jsx';
import AddSubaccount from './screens/AddSubaccount.jsx';
import SettleNow from './screens/SettleNow.jsx';
import AdminOverview from './screens/AdminOverview.jsx';
import AdminSettings from './screens/AdminSettings.jsx';
import EmailSources from './screens/EmailSources.jsx';
import AgentHealth from './screens/AgentHealth.jsx';
import Inbox from './screens/Inbox.jsx';
import CatalogAdmin from './screens/CatalogAdmin.jsx';
import More from './screens/More.jsx';
import Activity from './screens/Activity.jsx';
import AdminActivity from './screens/AdminActivity.jsx';
import Balances from './screens/Balances.jsx';
import LogMovementModal from './screens/LogMovementModal.jsx';
import BottomNav from './components/BottomNav.jsx';
import DesktopNav from './components/DesktopNav.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';

// Map prototype HTML filenames to internal screen ids.
// The prototypes use plain anchor links like <a href="Loop Catalog.html">.
// We intercept those clicks and translate them into internal route changes.
const HTML_TO_SCREEN = {
  'Investment Home.html': 'home',
  'Step Detail.html':     'step-detail',
  'Settle Now.html':      'settle-now',
  'Settlements.html':     'settlements',
  'Add Sub-account.html': 'add-subaccount',
  'Email Sources.html':   'email-sources',
  'Inbox.html':           'inbox',
};

// T-017: the investor-facing browse-&-subscribe Catalog is retired —
// assignment (admin) auto-subscribes, so there is nothing for an investor
// to shop for. Catalog ADMIN ('catalog-admin') is a different surface and
// stays. Screens/links that pointed at '#catalog' were removed with it.
// T-043: 'edit-allocations' retired with the allocations concept.
// T-042: 'admin-member-detail' (the drill-down) is retired — clicking an
// investor opens the act-as investor view directly, and the admin-only
// controls live on its 'admin-settings' tab. Old drill-down hashes hit the
// redirect below so muscle memory and stale links keep working.
const SCREEN_COMPONENTS = {
  'home':                Home,
  'catalog-admin':       CatalogAdmin,
  'step-detail':         StepDetail,
  'settle-now':          SettleNow,
  'settlements':         Settlements,
  'add-subaccount':      AddSubaccount,
  'admin-member-detail': AdminMemberDetailRedirect,
  'admin-settings':      AdminSettings,
  'email-sources':       EmailSources,
  'agent-health':        AgentHealth,
  'inbox':               Inbox,
  'more':                More,
  'activity':            Activity,
  'balances':            Balances,
};

// Screens that admins (no linked member) should be able to reach. Their
// 'home' is the cross-investor AdminOverview instead of the investor's
// hurdle dashboard.
const ADMIN_SCREEN_COMPONENTS = {
  'home':                AdminOverview,
  'admin-member-detail': AdminMemberDetailRedirect,
  'catalog-admin':       CatalogAdmin,
  'email-sources':       EmailSources,
  'agent-health':        AgentHealth,
  'inbox':               Inbox,
  // Every household's ledger with the investor Activity filters + a Who
  // filter (owner request 2026-09-22). While acting as an investor the same
  // hash renders that investor's own Activity tab instead.
  'activity':            AdminActivity,
  // T-011: Add sub-account straight from a member drill-down (no act-as).
  // Wrapper resolves params.memberId to the holder row, then renders the
  // same investor screen with that member as the holder.
  'add-subaccount':      AdminAddSubaccount,
};

// When an admin "acts as" an investor, we stash the target member id here so
// the choice survives in-app navigations and hard reloads. The admin sees the
// investor's own screens (from SCREEN_COMPONENTS) rendered for this member,
// while their writes are still attributed to them (admin) by the DB trigger.
const ACT_AS_KEY = 'finops:inv:actAs';

// Parse a URL hash like "step-detail?stepId=abc&foo=bar" into
// { screen, params }. Used both at mount and on hashchange.
function parseHash(hash) {
  const raw = (hash || '').replace('#', '');
  const [screen, query] = raw.split('?');
  const params = query
    ? Object.fromEntries(new URLSearchParams(query).entries())
    : {};
  return { screen, params };
}

export default function App() {
  const [currentScreen, setCurrentScreen] = useState(() => {
    const { screen } = parseHash(typeof window !== 'undefined' ? window.location.hash : '');
    return SCREEN_COMPONENTS[screen] ? screen : 'home';
  });
  const [screenParams, setScreenParams] = useState(() => {
    const { screen, params } = parseHash(typeof window !== 'undefined' ? window.location.hash : '');
    return SCREEN_COMPONENTS[screen] ? params : {};
  });

  const [memberStatus, setMemberStatus] = useState({ status: 'loading', member: null, profile: null, error: null });

  // Global log-movement modal. The bottom nav's center "Log" action and the
  // Home quick-actions dispatch `finops:open-log`; we render one shared
  // LogMovementModal so logging works from any screen (it's the investor's
  // most frequent action). No preset loop → the modal shows its own loop +
  // step pickers (auto-selected when the holder has a single loop).
  const [logOpen, setLogOpen] = useState(false);
  useEffect(() => {
    const open = () => setLogOpen(true);
    if (typeof window === 'undefined') return;
    window.addEventListener('finops:open-log', open);
    return () => window.removeEventListener('finops:open-log', open);
  }, []);

  // Admin "act as investor" state. actingMemberId persists across navigations
  // (and reloads via sessionStorage); actingMember is the resolved members row.
  const [actingMemberId, setActingMemberId] = useState(() => {
    try { return (typeof window !== 'undefined' && window.sessionStorage.getItem(ACT_AS_KEY)) || null; }
    catch { return null; }
  });
  const [actingMember, setActingMember] = useState(null);

  // Resolve current member once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) {
        if (!cancelled) setMemberStatus({ status: 'no-supabase', member: null, profile: null, error: null });
        return;
      }
      try {
        const { data: { user } } = await sb.auth.getUser();
        if (!user) {
          if (!cancelled) setMemberStatus({ status: 'not-logged-in', member: null, profile: null, error: null });
          return;
        }
        const { data: profile } = await sb.from('profiles').select('id, member_id, role').eq('id', user.id).single();
        // Admin without a linked member is the platform admin — gets the
        // cross-investor overview screen instead of an investor's Home.
        // (Admins who ALSO want to act as an investor sign in via their
        // member-linked profile, e.g. a separate executor account.)
        if (!profile?.member_id && profile?.role === 'admin') {
          if (!cancelled) setMemberStatus({ status: 'admin-overview', member: null, profile, error: null });
          return;
        }
        if (!profile?.member_id) {
          if (!cancelled) setMemberStatus({ status: 'no-member', member: null, profile, error: null });
          return;
        }
        const { data: member } = await sb.from('members').select('*').eq('id', profile.member_id).single();
        if (!cancelled) setMemberStatus({ status: 'ready', member, profile, error: null });
      } catch (err) {
        if (!cancelled) setMemberStatus({ status: 'error', member: null, profile: null, error: err });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hash-based navigation with optional params (encoded as a query string).
  const navigate = useCallback((screenId, params = {}) => {
    if (!SCREEN_COMPONENTS[screenId]) {
      console.warn('[Investment] Unknown screen:', screenId);
      return;
    }
    setCurrentScreen(screenId);
    setScreenParams(params || {});
    if (typeof window !== 'undefined') {
      const search = new URLSearchParams(params || {}).toString();
      window.location.hash = screenId + (search ? '?' + search : '');
    }
  }, []);

  // Admin: start acting as an investor. Renders the investor's own screens
  // for this member; writes stay attributed to the admin (DB trigger).
  // `screen` lets a caller land somewhere other than Home — the admin ledger
  // opens a row's household on ITS Activity tab.
  const beginActAs = useCallback((memberId, screen = 'home') => {
    if (!memberId) return;
    try { window.sessionStorage.setItem(ACT_AS_KEY, memberId); } catch { /* noop */ }
    setActingMemberId(memberId);
    navigate(SCREEN_COMPONENTS[screen] ? screen : 'home');
  }, [navigate]);

  // Admin: stop acting and return to the admin overview. (T-042: the
  // per-member drill-down is retired — the investor view IS the member
  // surface now, so exiting goes back to the cross-investor overview.)
  const exitActAs = useCallback(() => {
    try { window.sessionStorage.removeItem(ACT_AS_KEY); } catch { /* noop */ }
    setActingMemberId(null);
    setActingMember(null);
    navigate('home');
  }, [navigate]);

  // Resolve the acting member's row whenever the id changes (admins only).
  useEffect(() => {
    let cancelled = false;
    if (!actingMemberId) { setActingMember(null); return; }
    if (memberStatus.status !== 'admin-overview') return;
    (async () => {
      const sb = getSupabase();
      if (!sb) return;
      const { data } = await sb.from('members').select('*').eq('id', actingMemberId).maybeSingle();
      if (cancelled) return;
      if (data) {
        setActingMember(data);
      } else {
        // Stale / invalid id — clear it so we fall back to the admin overview.
        try { window.sessionStorage.removeItem(ACT_AS_KEY); } catch { /* noop */ }
        setActingMemberId(null);
      }
    })();
    return () => { cancelled = true; };
  }, [actingMemberId, memberStatus.status]);

  // Listen to hash changes (back/forward buttons).
  useEffect(() => {
    const onHashChange = () => {
      const { screen, params } = parseHash(window.location.hash);
      if (SCREEN_COMPONENTS[screen]) {
        setCurrentScreen(screen);
        setScreenParams(params);
      }
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // Intercept clicks on prototype-style <a href="Foo.html"> links and route internally.
  const handleClick = useCallback((e) => {
    const a = e.target.closest && e.target.closest('a[href]');
    if (!a) return;
    const href = a.getAttribute('href');
    if (!href) return;

    // Internal route via hash (e.g. #balances or #step-detail?stepId=xyz)
    if (href.startsWith('#')) {
      const { screen, params } = parseHash(href);
      if (SCREEN_COMPONENTS[screen]) {
        e.preventDefault();
        navigate(screen, params);
      }
      return;
    }

    // Prototype-style HTML link, possibly with a query string:
    //   "Step Detail.html?stepId=abc" → navigate('step-detail', { stepId: 'abc' })
    const [base, query] = href.split('?');
    if (HTML_TO_SCREEN[base]) {
      e.preventDefault();
      const params = query
        ? Object.fromEntries(new URLSearchParams(query).entries())
        : {};
      navigate(HTML_TO_SCREEN[base], params);
    }
  }, [navigate]);

  // Render the current screen.
  const ScreenComponent = SCREEN_COMPONENTS[currentScreen] || Home;

  if (memberStatus.status === 'loading') return <StatusScreen tone="info">Loading…</StatusScreen>;
  if (memberStatus.status === 'no-supabase') {
    return (
      <StatusScreen tone="error">
        <strong>Supabase client not available.</strong>
        <p>The investment module needs <code>window.sb</code> from the parent app.</p>
      </StatusScreen>
    );
  }
  if (memberStatus.status === 'not-logged-in') return <StatusScreen tone="info">Please log in to the FinOps app.</StatusScreen>;
  if (memberStatus.status === 'admin-overview') {
    // Admin chose to act as a specific investor: render that investor's own
    // screens for the target member while keeping the admin profile (so admin
    // RLS still authorizes writes, and the DB trigger attributes them to the
    // admin). A sticky banner keeps the context obvious + offers an exit.
    if (actingMemberId) {
      if (!actingMember) {
        return <StatusScreen tone="info">Loading investor view…</StatusScreen>;
      }
      const ActingScreen = SCREEN_COMPONENTS[currentScreen] || Home;
      return (
        <div onClick={handleClick} style={{ minHeight: '100vh' }}>
          <ActingAsBanner member={actingMember} onExit={exitActAs} />
          {/* T-042: the acting navs carry the extra Admin tab — the admin's
              controls for this household live there, on the investor view. */}
          <DesktopNav currentScreen={currentScreen} navigate={navigate} adminTab />
          {/* T-018: every screen mounts inside an ErrorBoundary — one bad
              data row degrades to a card-level error instead of unmounting
              the banner/navs with it. Keyed so navigating resets the crash. */}
          <ErrorBoundary key={'acting:' + actingMemberId + ':' + currentScreen}>
            <ActingScreen
              member={actingMember}
              profile={memberStatus.profile}
              navigate={navigate}
              params={screenParams}
              actingAsAdmin
            />
          </ErrorBoundary>
          <BottomNav currentScreen={currentScreen} navigate={navigate} adminTab />
          {logOpen && (
            <LogMovementModal
              member={actingMember}
              profile={memberStatus.profile}
              onClose={() => setLogOpen(false)}
            />
          )}
        </div>
      );
    }
    const AdminScreen = ADMIN_SCREEN_COMPONENTS[currentScreen] || AdminOverview;
    return (
      <div onClick={handleClick} style={{ minHeight: '100vh' }}>
        <ErrorBoundary key={'admin:' + currentScreen}>
          <AdminScreen profile={memberStatus.profile} navigate={navigate} params={screenParams} beginActAs={beginActAs} />
        </ErrorBoundary>
      </div>
    );
  }
  if (memberStatus.status === 'no-member') {
    return (
      <StatusScreen tone="warning">
        <strong>Your profile isn't linked to a member yet.</strong>
        <p>Ask the admin to assign you in Settings → Executors.</p>
      </StatusScreen>
    );
  }
  if (memberStatus.status === 'error') {
    return (
      <StatusScreen tone="error">
        <strong>Couldn't load your investment profile.</strong>
        <p style={{ fontFamily: 'monospace', fontSize: 12 }}>{String(memberStatus.error?.message || memberStatus.error)}</p>
      </StatusScreen>
    );
  }

  return (
    <div onClick={handleClick} style={{ minHeight: '100vh' }}>
      <DesktopNav currentScreen={currentScreen} navigate={navigate} />
      <ErrorBoundary key={currentScreen}>
        <ScreenComponent member={memberStatus.member} profile={memberStatus.profile} navigate={navigate} params={screenParams} />
      </ErrorBoundary>
      <BottomNav currentScreen={currentScreen} navigate={navigate} />
      {logOpen && (
        <LogMovementModal
          member={memberStatus.member}
          profile={memberStatus.profile}
          onClose={() => setLogOpen(false)}
        />
      )}
    </div>
  );
}

// T-042: the admin member drill-down is retired — the investor view (act-as)
// is the member surface, with the admin-only controls on its Admin tab. This
// redirect keeps every stale '#admin-member-detail?memberId=…' hash working:
// on the admin branch it enters act-as for that member; anywhere else (an
// investor session, or already acting) it falls back to Home.
function AdminMemberDetailRedirect({ params, beginActAs, navigate }) {
  useEffect(() => {
    if (typeof beginActAs === 'function' && params?.memberId) beginActAs(params.memberId);
    else if (typeof navigate === 'function') navigate('home');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <StatusScreen tone="info">Opening investor view…</StatusScreen>;
}

// T-011: the platform admin (no linked member) opens Add sub-account for a
// target holder straight from the member drill-down. The admin branch renders
// screens without a `member` prop, so this wrapper resolves params.memberId
// to the members row and mounts the investor screen with the target as
// holder. Writes are authorized by the admin RLS policies and attributed by
// the stamp_actor trigger — the same authorization act-as relies on.
function AdminAddSubaccount({ profile, navigate, params }) {
  const memberId = params?.memberId;
  const [target, setTarget] = useState(null);      // null = loading
  const [missing, setMissing] = useState(false);
  useEffect(() => {
    let cancelled = false;
    setTarget(null);
    setMissing(false);
    if (!memberId) return undefined;
    (async () => {
      const sb = getSupabase();
      if (!sb) return;
      const { data } = await sb.from('members').select('*').eq('id', memberId).maybeSingle();
      if (cancelled) return;
      if (data) setTarget(data);
      else setMissing(true);
    })();
    return () => { cancelled = true; };
  }, [memberId]);
  if (!memberId || missing) {
    return (
      <StatusScreen tone="warning">
        <strong>No account holder selected.</strong>
        <p>Open an investor from the admin overview, then use "＋ Add sub-account" on their page.</p>
      </StatusScreen>
    );
  }
  if (!target) return <StatusScreen tone="info">Loading holder…</StatusScreen>;
  return <AddSubaccount member={target} profile={profile} navigate={navigate} params={params} />;
}

// Sticky banner shown while an admin is acting as an investor. Inline styles
// (not classes) so it renders identically regardless of the prefixed bundle
// CSS, and stays pinned above the investor screen it wraps.
function ActingAsBanner({ member, onExit }) {
  const name = member?.display_name || member?.name || 'investor';
  return (
    <div
      style={{
        position: 'sticky', top: 0, zIndex: 1000,
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '10px 16px',
        background: '#fef3c7', borderBottom: '1px solid #fcd34d',
        color: '#92400e',
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 800, letterSpacing: '0.02em' }}>● ACTING AS {name.toUpperCase()}</span>
      <span style={{ color: '#b45309' }}>
        You're seeing exactly what {name} sees, with all their controls — plus your Admin tab. Changes are recorded as made by you (admin).
      </span>
      <button
        type="button"
        onClick={onExit}
        style={{
          marginLeft: 'auto',
          padding: '6px 14px',
          background: '#92400e', color: '#fff',
          border: 'none', borderRadius: 8,
          fontWeight: 700, fontSize: 12.5, cursor: 'pointer', whiteSpace: 'nowrap',
        }}
      >
        Exit to admin →
      </button>
    </div>
  );
}

function StatusScreen({ tone = 'info', children }) {
  const colors = {
    info:    { bg: '#eef2ff', border: '#c7d2fe', text: '#3730a3' },
    warning: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
    error:   { bg: '#fef2f2', border: '#fecaca', text: '#9f1239' },
  }[tone];
  return (
    <div style={{
      padding: 24, margin: 24,
      border: `1px solid ${colors.border}`,
      borderRadius: 12,
      background: colors.bg,
      color: colors.text,
      fontFamily: 'system-ui, -apple-system, sans-serif',
      maxWidth: 640,
    }}>
      {children}
    </div>
  );
}
